import { getProcessStatusColor } from './styles'
import { Form, Modal, Input, Upload, message as antdMessage } from 'antd'
import React, { useState } from 'react'
import { HeadSection, ErrorHandler, AkaTypography } from 'components'
import { TFunction, useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { SEND_USER_MESSAGE_FOR_APPROVAL } from '../../core/mutations'
import { APPROVAL_FEED_LIST } from '../../core/queries'
import { TenantType } from '../../../generated/types'
import { ProcessStatusType } from './ProcessStatus'
import { ApolloError } from 'apollo-client'
import { ExecutionResult } from 'graphql'
import { useParams } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useAntdMessageApi } from 'providers'
import { InboxOutlined } from '@ant-design/icons'
import type { UploadProps, UploadFile } from 'antd'

const FormItem = Form.Item<{ userMessage: string }>
const { TextArea } = Input
const { Dragger } = Upload

interface UserMessageModal {
	open: boolean
	closeMessageModal: () => void
	contentId: string
	status: ProcessStatusType
	tenant: TenantType
	loading: boolean
	error: ApolloError | undefined
	onSubmit: (userMessage: string, files: File[]) => Promise<ExecutionResult>
}

export interface ReportUserMessageModal {
	reportId: string
	systemId: string
	tenant: TenantType
	open: boolean
	closeMessageModal: () => void
	status: ProcessStatusType
}

export interface ContentCheckUserMessageModal extends Omit<
	ReportUserMessageModal,
	'reportId'
> {
	contentCheckId: string
}

export const ContentCheckUserMessageModal = ({
	contentCheckId,
	systemId,
	...restProps
}: ContentCheckUserMessageModal) => {
	const { quarter } = useParams<{ quarter?: string }>()
	// eslint-disable-next-line no-unsafe-optional-chaining
	const [year, quarterNum] = quarter?.split('-Q') ?? []
	const [sendUserMessage, { error, loading }] = useMutation(
		SEND_USER_MESSAGE_FOR_APPROVAL,
		{
			refetchQueries: [
				{
					query: APPROVAL_FEED_LIST,
					variables: {
						systemId,
						quarter: quarterNum,
						year
					}
				}
			]
		}
	)
	const handleSubmit = async (userMessage: string, files: File[]) => {
		return await sendUserMessage({
			variables: {
				contentCheckId,
				softwareSystemId: systemId,
				userMessage: userMessage,
				files
			}
		})
	}
	return (
		<UserMessageModal
			{...restProps}
			contentId={contentCheckId}
			loading={loading}
			error={error}
			onSubmit={handleSubmit}
		/>
	)
}

export const ReportUserMessageModal = ({
	reportId,
	systemId,
	...restProps
}: ReportUserMessageModal) => {
	const { quarter } = useParams<{ quarter?: string }>()
	// eslint-disable-next-line no-unsafe-optional-chaining
	const [year, quarterNum] = quarter?.split('-Q') ?? []
	const [sendUserMessage, { error, loading }] = useMutation(
		SEND_USER_MESSAGE_FOR_APPROVAL,
		{
			refetchQueries: [
				{
					query: APPROVAL_FEED_LIST,
					variables: {
						systemId,
						quarter: quarterNum,
						year
					}
				}
			]
		}
	)
	const handleSubmit = async (userMessage: string, files: File[]) => {
		return await sendUserMessage({
			variables: {
				systemReportId: reportId,
				softwareSystemId: systemId,
				userMessage: userMessage,
				files
			}
		})
	}
	return (
		<UserMessageModal
			{...restProps}
			contentId={reportId}
			loading={loading}
			error={error}
			onSubmit={handleSubmit}
		/>
	)
}
const validationSchema = (t: TFunction) =>
	Yup.object().shape({
		userMessage: Yup.string()
			.trim()
			.required(t('approval.approvalList.messageModal.errorNoMessage'))
			.max(20000, t('approval.approvalList.messageModal.errorMaxMessage'))
	})

const UserMessageModal = ({
	open,
	closeMessageModal,
	contentId,
	status,
	tenant,
	loading,
	error,
	onSubmit
}: UserMessageModal) => {
	const [t] = useTranslation()
	const messageApi = useAntdMessageApi()

	// Note: this form is a weird mix of formik and antd Form.
	// The validation status is managed by formik, but the values
	// are managed by the antd Form component.
	// Setting <input value={...} /> has no effect while the input is
	// wrapped in a Form.Item.
	const [antdForm] = Form.useForm()
	const [fileList, setFileList] = useState<UploadFile[]>([])

	const {
		handleSubmit,
		handleChange,
		handleBlur,
		values,
		errors,
		touched,
		resetForm
	} = useFormik({
		initialValues: { userMessage: '' },
		onSubmit: async ({ userMessage }) => {
			const files = fileList.map(f => f.originFileObj as File).filter(Boolean)
			await onSubmit(userMessage, files).then(_ => {
				messageApi?.open({
					type: 'success',
					content: t('approval.approvalList.messageModal.success')
				})
				resetForm() // reset validation state
				antdForm.resetFields() // reset values
				setFileList([])
				closeMessageModal()
			})
		},
		validationSchema: validationSchema(t)
	})

	const uploadProps: UploadProps = {
		name: 'file',
		multiple: false,
		maxCount: 1,
		accept:
			'image/jpeg, image/jpg, image/png, image/mpg, image/mpeg, application/zip, application/pdf',
		customRequest({ onSuccess }) {
			setTimeout(() => onSuccess && onSuccess('ok'), 0)
		},
		fileList,
		onRemove(file) {
			setFileList(prev => prev.filter(f => f.uid !== file.uid))
		},
		onChange({ fileList: newFileList, file }) {
			const { status } = file
			if (status === 'done') {
				setFileList(newFileList)
			} else if (status === 'error') {
				antdMessage.error(`${file.name} Fehler aufgetreten.`)
			} else {
				setFileList(newFileList)
			}
		},
		showUploadList: {
			showRemoveIcon: true
		}
	}

	return (
		<Modal
			bodyStyle={{ margin: 0, padding: 0 }}
			width={'50%'}
			open={open}
			onCancel={closeMessageModal}
			onOk={() => handleSubmit()}
			cancelText={t('approval.approvalList.messageModal.exitModal')}
			okText={t('approval.approvalList.messageModal.sendMessage')}
			confirmLoading={loading}
			title={
				<HeadSection
					id={contentId}
					title={t('approval.approvalList.messageModal.title', {
						tenantName: tenant.name
					})}
					tags={[]}
					logo={
						tenant?.logo && {
							url: tenant.logo.url,
							alt: t('approval.approvalList.reportCard.logoAlt', {
								tenant: tenant.name
							})
						}
					}
				/>
			}
		>
			<div
				style={{
					borderTop: `3px solid ${getProcessStatusColor(status)}`,
					padding: '10px 16px'
				}}
			>
				<ErrorHandler loading={loading} error={error}>
					<Form layout={'vertical'} form={antdForm}>
						<FormItem
							label={t('approval.approvalList.messageModal.label')}
							name={'userMessage'}
							style={{ margin: 0 }}
							validateStatus={
								touched.userMessage && errors.userMessage ? 'error' : ''
							}
							help={
								touched.userMessage && errors.userMessage
									? errors.userMessage
									: undefined
							}
						>
							<TextArea
								rows={15}
								placeholder={t(
									'approval.approvalList.messageModal.placeholder'
								)}
								name="userMessage"
								value={values.userMessage}
								onChange={handleChange}
								onBlur={handleBlur}
							/>
						</FormItem>
						<FormItem style={{ marginTop: 16 }}>
							<Dragger
								{...uploadProps}
								style={{
									marginTop: 16,
									display: fileList.length > 0 ? 'none' : 'block'
								}}
							>
								{fileList.length === 0 && (
									<>
										<p className="ant-upload-drag-icon">
											<InboxOutlined />
										</p>
										<p className="ant-upload-text">
											{t('approval.approvalList.messageModal.uploadText')}
										</p>
										<p className="ant-upload-hint">
											{t('approval.approvalList.messageModal.uploadHint')}
										</p>
									</>
								)}
							</Dragger>
						</FormItem>
					</Form>
					<AkaTypography variant={'body2'}>
						{t('approval.approvalList.messageModal.messageCounter', {
							count: values.userMessage.length
						})}
					</AkaTypography>
				</ErrorHandler>
			</div>
		</Modal>
	)
}

export default UserMessageModal
